import { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { IconEdit, IconUpload } from '@tabler/icons-react';

import { AvatarGroupList } from '@/components/Avatar';
import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import { useFormatText } from '@/hooks/useFormatText';
import PhoneNumberInput from '@/pages/school/components/PhoneInput';
import {
  getAdminProfile,
  uploadAdminImage,
  updateAdminProfile,
} from '@/service/adminServices';
import useCommonStore from '@/store/useCommonStore';
import useUser from '@/store/useUser';
import { IAdminDetailsData } from '@/types';
import { getAssetUrl, formatLastActiveAt, formatUnixToLongDate } from '@/utils';

const Profile = () => {
  const { formatText } = useFormatText();
  const { loadingModal, setLoadingModal } = useCommonStore();
  const { user, setUser } = useUser();

  const [adminData, setAdminData] = useState<IAdminDetailsData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editName, setEditName] = useState('');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, setValue, getValues, reset, watch } = useForm<{
    phone: string;
    countryCode: string;
  }>();

  useEffect(() => {
    fetchProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfileData = async () => {
    setLoadingModal(true);
    try {
      const response = await getAdminProfile();
      if (response?.status) {
        setAdminData(response?.data);
        setUser({
          ...user,
          name: response?.data?.name,
          // email: response?.data?.email,
          profile_image: response?.data?.profile_pic,
        });
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    }
    setLoadingModal(false);
  };

  const handleEditClick = () => {
    setEditName(adminData?.name || user?.name || '');
    reset({
      phone: adminData?.phone_number || '',
      countryCode: adminData?.phone_code || '+965',
    });
    setIsEditing(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleCancel = () => {
    setPreviewImage(null);
    setSelectedFile(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      let assetId =
        typeof adminData?.asset === 'object' ? adminData?.asset?.id : undefined;

      if (selectedFile) {
        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('folder', 'admin');
        const uploadRes = await uploadAdminImage(formData);
        if (uploadRes?.status) {
          assetId = uploadRes?.data?.id;
        }
      }

      const { phone, countryCode } = getValues();
      const response = await updateAdminProfile({
        name: editName,
        phone_code: countryCode,
        phone_number: phone,
        ...(assetId ? { asset_id: assetId } : {}),
      });

      if (response?.status) {
        await fetchProfileData();
        setPreviewImage(null);
        setSelectedFile(null);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving profile:', error);
    }
    setIsSaving(false);
  };

  const profileImage =
    previewImage ??
    (getAssetUrl(adminData?.asset) ||
      adminData?.profile_pic ||
      adminData?.user_img);

  if (loadingModal) {
    return (
      <div className="border border-neutral-light-200 rounded-2xl p-6 animate-pulse">
        <div className="flex justify-between items-center mb-6">
          <div className="h-5 w-40 bg-neutral-light-100 rounded" />
          <div className="h-8 w-8 bg-neutral-light-100 rounded-lg" />
        </div>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-18 h-18 rounded-full bg-neutral-light-100" />
          <div className="space-y-2">
            <div className="h-6 w-32 bg-neutral-light-100 rounded" />
            <div className="h-5 w-22 bg-neutral-light-100 rounded-full" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 w-24 bg-neutral-light-100 rounded" />
              <div className="h-5 w-32 bg-neutral-light-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-neutral-light-200 rounded-2xl p-6">
      {/* Card Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-semibold text-neutral-primary">
          {formatText('settings.profile.profile-information')}
        </h3>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              label={formatText('common.actions.cancel')}
              onClick={handleCancel}
              disabled={isSaving}
            />
            <Button
              variant="primary"
              label={formatText('common.actions.save-changes')}
              onClick={handleSave}
              loading={isSaving}
            />
          </div>
        ) : (
          <Button
            variant="outline"
            className="w-11! h-11! p-0!"
            icon={<IconEdit size={20} className="w-5 h-5" />}
            onClick={handleEditClick}
          />
        )}
      </div>

      {/* Avatar + Name + Role Badge */}
      <div className="flex items-start gap-6">
        <div className="relative shrink-0">
          <AvatarGroupList
            list={[
              {
                id: adminData?.id || '',
                name: adminData?.name || user?.name || '',
                image: profileImage,
              },
            ]}
            size={100}
            avatarClassName="w-25! h-25! text-4xl!"
            className="rounded-full overflow-hidden"
          />
          {isEditing && (
            <label className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 cursor-pointer">
              <IconUpload size={22} className="text-white" />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageChange}
              />
            </label>
          )}
        </div>
        <div className="w-full">
          <div className="mb-6">
            {isEditing ? (
              <Input
                name="name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={formatText('settings.profile.profile-information')}
                className="max-w-xs"
              />
            ) : (
              <p className="text-2xl font-semibold text-neutral-primary leading-7 capitalize">
                {adminData?.name || user?.name || '-'}
              </p>
            )}
            <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-success-light-50 text-success-secondary text-xs font-semibold">
              {adminData?.role?.name ||
                formatText('settings.profile.super-admin')}
            </span>
          </div>
          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-6">
            {/* Email ID */}
            <div className="flex flex-col gap-2">
              <span className="text-sm text-neutral-primary">
                {formatText('settings.profile.email-id')}
              </span>
              <span className="text-base font-semibold text-neutral-primary break-all">
                {adminData?.email || user?.email || '-'}
              </span>
            </div>

            {/* Phone Number */}
            <div className="flex flex-col gap-2">
              {isEditing ? (
                <PhoneNumberInput
                  name="phone"
                  countryFlag
                  label={formatText('settings.profile.phone-number')}
                  register={register}
                  setValue={setValue}
                  placeholder={formatText('settings.profile.phone-number')}
                  countrySelected={watch('countryCode')}
                />
              ) : (
                <>
                  <span className="text-sm text-neutral-primary">
                    {formatText('settings.profile.phone-number')}
                  </span>
                  <span className="text-base font-semibold text-neutral-primary">
                    {adminData?.phone_code && adminData?.phone_number
                      ? `${adminData.phone_code} ${adminData.phone_number}`
                      : '-'}
                  </span>
                </>
              )}
            </div>

            {/* Account Created */}
            <div className="flex flex-col gap-2">
              <span className="text-sm text-neutral-primary">
                {formatText('settings.profile.account-created')}
              </span>
              <span className="text-base font-semibold text-neutral-primary">
                {adminData?.created_at
                  ? formatUnixToLongDate(adminData?.created_at)
                  : '-'}
              </span>
            </div>

            {/* Last Login */}
            <div className="flex flex-col gap-2">
              <span className="text-sm text-neutral-primary">
                {formatText('settings.profile.last-login')}
              </span>
              <span className="text-base font-semibold text-neutral-primary">
                {adminData?.last_active_at
                  ? formatLastActiveAt(adminData.last_active_at)
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
